import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

// Mismo patrón que TodayFollowUpsWidget.jsx — GET /leads?min_follow_up_count=3
// &exclude_terminal_status=true, sin endpoint separado. FOLLOW_UP_LIMIT (3)
// hardcodeado aquí igual que en LeadDetail.js — ver esa nota sobre la
// ausencia de una constante compartida frontend/backend en este módulo.
const FOLLOW_UP_LIMIT = 3;

export function DiscardSuggestionsWidget({ refreshKey }) {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDiscardSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/leads', {
        params: {
          min_follow_up_count: FOLLOW_UP_LIMIT,
          exclude_terminal_status: true,
          limit: 50,
        },
      });
      setLeads(res.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch discard suggestions:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchDiscardSuggestions();
  }, [fetchDiscardSuggestions, refreshKey]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        <h2 className="text-sm font-semibold text-zinc-200">
          {t('aiLeadScanner.discardSuggestions.title')}
        </h2>
        {!loading && leads.length > 0 && (
          <span className="text-xs text-zinc-500">({leads.length})</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
        </div>
      ) : leads.length === 0 ? (
        <p className="text-sm text-zinc-500">{t('aiLeadScanner.discardSuggestions.empty')}</p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
          {leads.map((lead) => (
            <button
              key={lead.id}
              onClick={() => navigate(`/internal/ai-lead-scanner/leads/${lead.id}`)}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-left"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-200 truncate">{lead.business_name}</p>
                <span className="text-xs text-amber-400">
                  {t('aiLeadScanner.leadDetail.followUpCount', { count: lead.follow_up_count || 0, limit: FOLLOW_UP_LIMIT })}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
